// Base interfaces for DTOs
interface IDto {
  // Base interface for all DTOs
}

interface ICreateDto {
  // Base interface for create DTOs
}

// User Role enum (mirrors server-side enum)
export enum UserRole {
  SYSTEM_ADMIN = 'SYSTEM_ADMIN',
  UNIVERSITY_ADMIN = 'UNIVERSITY_ADMIN',
  FACULTY_ADMIN = 'FACULTY_ADMIN',
  PROFESSOR = 'PROFESSOR',
  COURSE_REPRESENTATIVE = 'COURSE_REPRESENTATIVE',
}

// User role information within UserWithRoleDTO
export interface UserRoleInfoDTO extends IDto {
  userRoleId: string;
  role: UserRole;
  domainId?: string;
  subjectId?: string;
  assignedAt: string;
  assignedBy: string;
  expiresAt?: string;
  isExpired: boolean;
}

// Base User DTO
export interface UserDTO extends IDto {
  id: string;
  tenantId: string;
  email: string;
  name: string;
  cognitoId?: string;
  status: 'invited' | 'active' | 'inactive';
  createdAt: string;
  updatedAt?: string;
  isDeleted?: boolean;
}

// DTO for creating new users
export interface CreateUserDTO extends ICreateDto {
  tenantId: string;
  email: string;
  name: string;
  cognitoId?: string;
}

// User with role DTO (main DTO for centralized user management)
export interface UserWithRoleDTO extends IDto {
  // User fields
  id: string;
  tenantId: string;
  email: string;
  name: string;
  cognitoId?: string;
  status: 'invited' | 'active' | 'inactive';
  createdAt: string;
  updatedAt?: string;
  isDeleted?: boolean;

  // Role information (array to support multiple roles, null if user has no roles)
  roles: UserRoleInfoDTO[] | null;
}

// Request DTO for querying users with roles
export interface GetUsersWithRolesRequestDTO extends IDto {
  // Filtering parameters
  role?: UserRole;
  tenantId?: string;
  domainId?: string;
  subjectId?: string;
  status?: 'invited' | 'active' | 'inactive';
  email?: string;
  name?: string;
  includeExpiredRoles?: boolean;
  includeUsersWithoutRoles?: boolean;

  // Pagination parameters
  limit?: number;
  offset?: number;
  nextToken?: string;

  // Sorting parameters
  sortBy?: 'name' | 'email' | 'createdAt' | 'role' | 'assignedAt';
  sortOrder?: 'asc' | 'desc';
}

// Response DTO for users with roles with pagination support
export interface UsersWithRolesResponseDTO extends IDto {
  users: UserWithRoleDTO[];
  totalCount: number;
  limit?: number;
  offset?: number;
  nextToken?: string;
  hasMore: boolean;
}

// User invitation DTOs
export interface InviteUserDTO extends ICreateDto {
  email: string;
  name?: string;
  temporaryPassword: string;
  tenantId: string;
  role?: string;
  domainId?: string;
  subjectId?: string;
  assignedBy?: string;
}

export interface InvitationResponseDTO extends IDto {
  success: boolean;
  message?: string;
  errorMessage?: string;
}

// DTO for checking user existence
export interface UserExistsCheckDTO extends IDto {
  email: string;
  exists: boolean;
  user?: UserDTO;
}

// Unified DTOs for invite-with-role endpoint
export interface InviteUserWithRoleDTO extends ICreateDto {
  email: string;
  name?: string;
  role: string;
  tenantId: string;
  domainId?: string;
  subjectId?: string;
  assignedBy: string;
  expiresAt?: string;
}

export interface InvitationWithRoleResponseDTO extends IDto {
  success: boolean;
  message: string;
  errorMessage?: string;
  userCreated: boolean;
  cognitoInvitationSent: boolean;
  roleAssigned: boolean;
  user: UserDTO;
  userRole: any;
}
